import FormInput from "@/components/Form/FormInput";
import PhoneInput from "@/components/Form/PhoneInput";
import SelectInput from "@/components/Form/SelectInput";
import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { xor } from "lodash";
import { twMerge } from "tailwind-merge";

import { AutocompleteValue, FormColumn, FormFieldType } from "./types";

type Props = {
  column: FormColumn;
  enabled?: boolean; // Overrides column.enabled
  rowIndex: number;
  colIndex: number;
  formHandler: UseFormReturn<any>;
  regexPattern?: RegExp;
  patternError?: string;
  required?: boolean;
  value: string;
  validationFunction?: (value: string) => boolean;
  autocompleteOptions?: AutocompleteValue[];
  onAutocompleteOptionSelected?: (
    row: number,
    option: AutocompleteValue,
  ) => void;
  selectInputMenuPortalTarget?: HTMLElement | null;
};

const uniques = (arr: any[]) => xor(...arr.map((a) => [a]));
const duplicates = (arr: any[]) => xor(arr, uniques(arr));

export function GridFormCell({
  colIndex,
  column: {
    enabled,
    fieldType,
    name,
    options,
    defaultValue,
    unique,
    uniqueError,
    placeholder,
    readOnlyContent,
    onSelectChange,
  },
  enabled: enabledOverride,
  formHandler,
  regexPattern,
  patternError,
  required,
  rowIndex,
  value,
  validationFunction,
  autocompleteOptions,
  onAutocompleteOptionSelected,
  selectInputMenuPortalTarget,
}: Props) {
  const { t } = useTrans();
  const {
    control,
    register,
    formState: { errors },
    formState: { defaultValues },
  } = formHandler;

  const formRootName = useMemo(
    () => Object.keys(defaultValues ?? {})[0],
    [defaultValues],
  );

  const enabledValue = useMemo(() => {
    return typeof enabled === "function" ? enabled(rowIndex) : enabled;
  }, [enabled, rowIndex]);

  const fieldName = useMemo(
    () => `${formRootName}.${rowIndex}.${name}`,
    [formRootName, rowIndex, name],
  );

  const showAutocompleteOptions = useMemo(
    () =>
      autocompleteOptions &&
      onAutocompleteOptionSelected &&
      autocompleteOptions?.length > 0,
    [autocompleteOptions, onAutocompleteOptionSelected],
  );

  const isValidUnique = useCallback(
    (value: string) => {
      if (!unique) return true;
      const allValues = formHandler
        .getValues()
        [formRootName].map((row: any) => row[name]);
      return duplicates(allValues).includes(value)
        ? uniqueError || false
        : true;
    },
    [unique, formHandler, formRootName, name, uniqueError],
  );

  const validationAndUnique = useMemo(() => {
    if (!validationFunction) return (value: string) => isValidUnique(value);
    return (value: string) => validationFunction(value) && isValidUnique(value);
  }, [validationFunction, isValidUnique]);

  const input = useMemo(() => {
    if (!fieldType || fieldType === FormFieldType.TEXT) {
      return (
        <FormInput
          name={fieldName}
          disabled={!enabledOverride || !enabledValue}
          defaultValue={defaultValue}
          value={value}
          error={(errors?.[formRootName] as any)?.[rowIndex]?.[name]}
          register={register}
          regexPattern={regexPattern}
          patternError={patternError}
          required={required}
          validationFunction={validationAndUnique}
          showErrorInTooltip
        />
      );
    }

    if (fieldType === FormFieldType.PHONE) {
      return (
        <PhoneInput
          name={fieldName}
          control={control}
          regexPattern={regexPattern}
          validationFunction={validationAndUnique}
          patternError={patternError}
        />
      );
    }

    if (fieldType === FormFieldType.READ_ONLY && readOnlyContent) {
      return readOnlyContent(rowIndex);
    }

    return (
      <SelectInput
        name={fieldName}
        control={control}
        defaultValue={options?.find((o: any) => o.value === defaultValue)}
        isDisabled={!enabledOverride || !enabledValue}
        options={options as any}
        placeholder={placeholder ?? t("formLabels:chooseAlternative")}
        menuPortalTarget={selectInputMenuPortalTarget}
        validationFunction={validationAndUnique}
        onSelectChange={onSelectChange}
      />
    );
  }, [
    fieldType,
    fieldName,
    enabledOverride,
    enabledValue,
    defaultValue,
    value,
    errors,
    formRootName,
    rowIndex,
    name,
    register,
    regexPattern,
    patternError,
    required,
    control,
    options,
    t,
    validationAndUnique,
    placeholder,
    readOnlyContent,
    selectInputMenuPortalTarget,
    onSelectChange,
  ]);

  return (
    <div className={twMerge("relative", colIndex === 0 ? "mr-1" : "m-1")}>
      {input}
      {showAutocompleteOptions && (
        <div className="absolute z-10 w-full border-2 border-t-0 border-black bg-white">
          {autocompleteOptions!.map((option, index) => (
            <div
              key={index}
              className="font-dm-sans hover:bg-grey1 cursor-pointer border-b px-2 py-1 text-sm"
              onClick={() => onAutocompleteOptionSelected!(rowIndex, option)}
            >
              {option[name]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
